import { toLower } from 'lodash-es';
import { Dictionary } from 'src/app/shared/utilities/types.utils';
import { catchError, from, map, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { validString } from 'src/app/shared/utilities/types.utils';

export interface LocaleImportDetail {
  locale: string;
  localeData: any | null;
}

/**
 * Returns an international collator compare function
 * @param locale The locale
 */
export function getIntlCollator(locale: string): (x: string, y: string) => number | null {
  try {
    return new Intl.Collator(locale.split('-')[0]).compare;
  } catch {
    return null;
  }
}

/**
 * Gets the default language from the browser.
 * @returns string
 */
export function getDefaultLocale(): string {
  const { language } = window?.navigator;

  if (validString(language)) {
    return language;
  }

  return 'en-US';
}

/**
 * Remove last part of locale
 * @returns string
 */
export function parseLanguage(locale: string): string {
  if (validString(locale)) {
    const [language = '', ...region] = locale.split('-');
    region.pop();
    return [language, ...region].join('-');
  }

  return '';
}

/**
 * Import locale from angular library
 * @param locale
 * @returns
 */
export function importAngularLocale(locale: string): Observable<LocaleImportDetail> {
  return from(
    import(
      /* webpackExclude: /\.(d\.ts|js\.map)$/, webpackMode: "lazy", webpackChunkName: "locale-[request]" */
      `node_modules/@angular/common/locales/${locale}`
    )
  ).pipe(
    map((localeData: any): LocaleImportDetail => ({ locale, localeData: localeData.default })),
    catchError((err: Error) => of({ locale: '', localeData: null }))
  );
}

/**
 * Import a list of supported locales
 * @param locale
 * @returns
 */
export function getSupportedLocaleList(): Observable<string[] | null> {
  return from(import('../../auto-generated/locale-map/locale-map.json')).pipe(
    map((supportedLocales): string[] => Array.from(supportedLocales)),
    catchError((err) => of(null))
  );
}

/**
 * Custom Operator to find a supported locale
 * @param localeId The locale ID currently being used
 */
export function findSupportedLocale<T>(locale: string): OperatorFunction<string[], string> {
  return pipe(
    map((supportedLocales: string[]) => new Set(supportedLocales)),
    map((supportedLocales: Set<string>) => getPotentialLocaleList(locale).find((locale) => supportedLocales.has(locale)))
  );
}

/**
 * Creates a list of potential locales from locale parameter
 * @param locale
 * @returns list of potential locales
 */
export const getPotentialLocaleList = (locale: string): string[] => {
  let parsedLang = parseLanguage(locale);
  const localeSet = new Set([locale, parsedLang]);
  const langArr = parsedLang.split('-');

  if (langArr.length > 1) {
    langArr.forEach(() => {
      const currLang = parseLanguage(parsedLang);
      localeSet.add(currLang);
      parsedLang = currLang;
    });
  }

  return Array.from(localeSet);
};

/**
 * Converts non-standard locales to CLDR locales
 * @param locale The locale to convert
 */
export const localeToCLDR = (locale: string = ''): string => {
  const languageLowercased = toLower(locale);
  const localeMap: Dictionary<string> = {
    'zh-cn': 'zh-Hans',
    'zh-tw': 'zh-Hant',
    'zh-hk': 'zh-Hant-HK',
    'zh-sg': 'zh-Hant',
    'zh-mo': 'zh-Hant-MO',
    'ko-kr': 'ko',
    'da-dk': 'da',
    'pt-br': 'pt',
    'he-il': 'he',
    'sv-se': 'sv',
  };

  // Fix items where language equals the country, such as fr-FR or de-DE. Exclusion is pt-pt where cldr does have this.
  const [language, country] = languageLowercased.split('-');
  if (validString(languageLowercased) && language === country && languageLowercased !== 'pt-pt') {
    return language;
  }

  return localeMap[languageLowercased] ? localeMap[languageLowercased] : locale;
};

/**
 * Extract the language part from the locale
 * @param locale: Locale
 * @returns Language part of the locale
 */
export const getLocaleLanguage = (locale: string): string => {
  return validString(locale) ? locale.split('-')[0] : '';
};
